/**
 * Added by Amisha on 2026-06-12.
 * Reason: centralize the Janus AppView leave-confirmation modal state so
 * route guards can pause shell navigation until the user confirms.
 */
import { Injectable } from '@angular/core';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';

@Injectable({
  providedIn: 'root'
})
export class AppViewLeaveConfirmationService {
  modalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Are you sure you want to leave this application?',
    modalSubDescp: 'Any unsaved changes will be lost.',
    yesNoBtn: true,
    replaceYesWith: 'LEAVE',
    replaceNoWith: 'CANCEL'
  };

  private pendingConfirmation: Promise<boolean> | null = null;
  private resolvePendingConfirmation:
    | ((shouldLeave: boolean) => void)
    | null = null;
  // Added by Amisha on 2026-06-26.
  // Reason: track whether the active Janus AppView actually has form edits so
  // the shared leave modal only appears for real unsaved changes.
  private hasUnsavedChanges = false;
  // Added by Amisha on 2026-07-09.
  // Reason: some Janus forms update on blur, so the service needs a live
  // callback into AppView to re-check unsaved changes right before navigation.
  private hasUnsavedChangesEvaluator: (() => boolean) | null = null;

  // Added by Amisha on 2026-06-26.
  // Reason: let AppView push its current dirty state into the shared Janus
  // leave-confirmation service for shell/header navigation checks.
  setUnsavedChanges(value: boolean): void {
    this.hasUnsavedChanges = value;
  }

  // Added by Amisha on 2026-07-09.
  // Reason: let the shared Janus leave-confirmation service query the latest
  // AppView dirty state after flushing the focused control.
  setUnsavedChangesEvaluator(evaluator: (() => boolean) | null): void {
    this.hasUnsavedChangesEvaluator = evaluator;
  }

  // Added by Amisha on 2026-06-26.
  // Reason: allow navigation immediately when the active Janus AppView has no
  // unsaved changes instead of always opening the confirmation modal.
  requestConfirmation(): Promise<boolean> {
    // Added by Amisha on 2026-07-09.
    // Reason: blur the active field before checking so updateOn:'blur' Janus
    // controls can push their latest edit into the form dirty state.
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }

    // Added by Amisha on 2026-07-09.
    // Reason: refresh the cached unsaved-changes value from the live AppView
    // callback before deciding whether the warning is needed.
    if (this.hasUnsavedChangesEvaluator) {
      this.hasUnsavedChanges = this.hasUnsavedChangesEvaluator();
    }

    if (!this.hasUnsavedChanges) {
      return Promise.resolve(true);
    }

    if (this.pendingConfirmation) {
      return this.pendingConfirmation;
    }

    this.modalData = {
      ...this.modalData,
      open: true
    };

    this.pendingConfirmation = new Promise<boolean>(resolve => {
      this.resolvePendingConfirmation = resolve;
    });

    return this.pendingConfirmation;
  }

  confirmLeave(): void {
    this.finishConfirmation(true);
  }

  cancelLeave(): void {
    this.finishConfirmation(false);
  }

  private finishConfirmation(shouldLeave: boolean): void {
    const resolver = this.resolvePendingConfirmation;

    this.resolvePendingConfirmation = null;
    this.pendingConfirmation = null;
    this.modalData = {
      ...this.modalData,
      open: false
    };

    resolver?.(shouldLeave);
  }
}
